'use client';
import { useEffect } from 'react';

import { useMediaPlayback } from '@/hooks/useMediaPlayback';
import { cn } from '@/lib/utils';

import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import { IconButton } from '@/decodo/components/atoms/IconButton';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/decodo/components/atoms/Text';
import { AccordionListComponent } from '@/decodo/components/molecules/accordion/AccordionList';
import { useAccordion } from '@/decodo/components/molecules/accordion/AccordionProvider';
import { Accordion } from '@/decodo/components/molecules/accordion/index';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContentDC';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

import WithHasUserInteracted, {
  useHasUserInteracted,
} from './WithHasUserInteracted';

import useForceRerender from '@/hooks/useForceRerender';
import { useScreenSize } from '@/hooks/useScreenSize';
import IconPause from '~/icons/pause-square.svg';
import IconPlay from '~/icons/play-square.svg';
import { VideoPlayerMobile } from './VideoPlayerMobile';

type StrapiMediaObject = {
  data?: {
    attributes?: {
      url?: string;
    };
  };
};

export type VideoAccordionProps = {
  header?: BlockHeader;
  accordions: {
    title: string;
    description: RichTextFieldStrapiProps;
    button?: LinkButtonStrapiProps;
    video?: {
      url?: string;
      src?: StrapiMediaObject | React.ReactNode;
    };
  }[];
};

const VideoAccordion = (props: VideoAccordionProps) => {
  const { header, accordions, ...rest } = props;
  const { isDesktop } = useScreenSize();

  const { openItemId, setOpenItemId } = useAccordion();

  const handleAccordionClick = (id: string) => {
    setOpenItemId(id);
  };

  const currentAccordion = accordions.find((_, i) => String(i) === openItemId);
  const videoToRender = currentAccordion?.video?.url;

  const {
    duration,
    handleMount,
    mediaRef,
    togglePlay,
    isPlaying,
    _setIsPlaying,
    _setMediaLength,
  } = useMediaPlayback<HTMLVideoElement>();

  useEffect(() => {
    handleMount();
    _setIsPlaying(true);
    // Note: keep `videoToRender` in the dependency array to ensure the video is updated automatically on timer end
  }, [videoToRender, mediaRef.current]);

  useForceRerender(!mediaRef.current);

  const PlayPauseButton = ({
    classNames,
  }: {
    classNames?: {
      iconButton: string;
    };
  }) => (
    <IconButton
      onClick={() => {
        setHasUserInteracted(true);
        togglePlay();
      }}
      noStyles
      className={classNames?.iconButton}
    >
      {isPlaying ? <IconPause /> : <IconPlay />}
    </IconButton>
  );

  const handleVideoClick = () => {
    if (isDesktop && !hasUserInteracted) {
      setHasUserInteracted(true);
    }
    togglePlay();
  };

  const formattedAccordionList =
    accordions.map((item) => ({
      header: (
        <Text
          variant='subtitle-l-d'
          onClick={() => handleAccordionClick(item.toString())}
          classNames={{ wrapper: 'font-serif' }}
        >
          {item.title}
        </Text>
      ),
      children: (
        <div className='flex flex-col gap-6'>
          <div className='block md:hidden'>
            <div className='relative rounded bg-gray-100 dark:bg-gray-800'>
              <VideoPlayerMobile src={item.video?.url} />
            </div>
          </div>
          <RichTextFieldStrapi block={item.description} />
          {item?.button && (
            <LinkButtonStrapi
              block={item.button}
              inner={{
                classNames: { root: 'w-full md:w-fit' },
                variant: 'outlined',
                color: isDesktop ? 'secondary' : 'primary',
              }}
            />
          )}
        </div>
      ),
    })) || [];

  const { hasUserInteracted, setHasUserInteracted } = useHasUserInteracted();

  return (
    <BlockWrapper
      {...rest}
      data-tracker-section={SECTION_NAMES.IMAGE_ACCORDIONS}
    >
      <div className='relative flex w-full'>
        <div className='min-h-[720px] w-full md:w-1/2'>
          <BlockHeaderContent
            {...header}
            alignment='left'
            classNames={{ header: '!mb-12' }}
          >
            <AccordionListComponent
              accordionList={formattedAccordionList}
              internal={{
                timer: {
                  durationSec: hasUserInteracted ? 0 : duration,
                },
              }}
            />
          </BlockHeaderContent>
        </div>
        {isDesktop && (
          <div
            className={cn(
              'absolute right-0 top-0 h-full bg-gray-100 md:flex md:items-center md:justify-center md:rounded-l md:text-center 2xl:rounded dark:bg-gray-800',
              'left-1/2 ml-8 h-[720px] w-[calc(50vw-2.5rem)] max-w-[720px] overflow-hidden',
              'hidden md:block'
            )}
          >
            <video
              id='desktop-video'
              onClick={handleVideoClick}
              ref={mediaRef}
              className='h-full w-auto object-cover'
              onLoad={handleMount}
              src={videoToRender}
              controls={false}
              muted
              autoPlay
              loop
              onLoadedData={(e) => {
                // have to manually set this here, otherwise the pause button logic breaks
                _setMediaLength(e.currentTarget.duration);
              }}
            />
            <PlayPauseButton
              classNames={{
                iconButton: cn(
                  'text-base-white dark:text-base-black',
                  'absolute bottom-[160px] left-0 z-50 m-4 h-6 w-6 stroke-2 md:bottom-0'
                ),
              }}
            />
          </div>
        )}
      </div>
    </BlockWrapper>
  );
};

// eslint-disable-next-line import/no-anonymous-default-export
export default function (props: VideoAccordionProps) {
  return (
    <WithHasUserInteracted>
      <Accordion>
        <VideoAccordion {...props} />
      </Accordion>
    </WithHasUserInteracted>
  );
}
